import React from 'react';

import { Text, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { SIGN_IN_CORE } from '../components/keys';

/**
 * Builds the "We have sent a sign-in link to <address>" sentence for the check-emails pages.
 *
 * The address is emphasized in place, so the sentence stays a single translatable string.
 *
 * @param email the address the link was sent to, when it is known.
 * @param options.withGenericFallback when the address is unknown, build the sentence with a generic
 * stand-in for the address instead of returning nothing.
 *
 * Opt in only where the address can never be known, as on the 2FA page: that page is always reached
 * through a backend redirect carrying no query params, so there is no address to show and the
 * stand-in is the only way to state that a link was sent.
 *
 * Leave it off where the address is normally known, as on the magic link page. Note that every step
 * is also reachable by URL (`/:step*`) and the wizard seeds no state, so an empty state there is an
 * anomaly — a hand-typed URL, or a reload after submitting — rather than proof a link was sent.
 * Rendering nothing is what that page has always done in that case.
 */
export const useSentLinkMessage = (
    email?: string,
    { withGenericFallback = false }: { withGenericFallback?: boolean } = {},
): React.ReactNode => {
    const { translateKey, translateAndReplaceWithComponents } = useTranslate();

    if (!email && !withGenericFallback) {
        return undefined;
    }

    return translateAndReplaceWithComponents(SIGN_IN_CORE.LINK_SENT_TO_EMAIL, {
        email: (
            <Text as="span" typography={Typography.subtitle1}>
                {email || translateKey(SIGN_IN_CORE.YOUR_EMAIL_ADDRESS)}
            </Text>
        ),
    });
};
